import { parseProductInfo } from './productParserService';
import { createProduct } from './productService';
import { uploadProductImage } from './uploadService';
import { translateProduct } from './translationService';
import { message } from 'antd';
import type { ProductImage, Product } from '../types/product';

export interface BatchProductItem {
  rawText: string;
  price: number;
  images: File[];
}

export interface BatchUploadResult {
  success: number;
  failed: number;
  errors: Array<{
    index: number;
    error: string;
  }>;
}

export const batchUploadProducts = async (
  products: BatchProductItem[],
  onProgress?: (current: number, total: number) => void
): Promise<BatchUploadResult> => {
  const result: BatchUploadResult = {
    success: 0,
    failed: 0,
    errors: []
  };

  for (let i = 0; i < products.length; i++) {
    try {
      const product = products[i];
      onProgress?.(i + 1, products.length);

      // 1. 解析商品信息
      const parsedInfo = await parseProductInfo(product.rawText);

      // 2. 翻译商品信息
      const translationResult = await translateProduct({
        title: parsedInfo.title,
        description: parsedInfo.description,
        tags: Array.isArray(parsedInfo.tags) ? parsedInfo.tags.join(', ') : '',
        comparison: {
          original: {
            material: parsedInfo.comparison.original.material,
            craftsmanship: parsedInfo.comparison.original.craftsmanship
          },
          replica: {
            material: parsedInfo.comparison.replica.material,
            craftsmanship: parsedInfo.comparison.replica.craftsmanship
          }
        }
      }, 'en');

      // 3. 上传图片
      const productId = `product-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const uploadedImages = await Promise.all(
        product.images.map(async (file, index) => {
          const type = index === 0 ? 'main' : 'detail';
          return await uploadProductImage(productId, file, type);
        })
      );

      // 过滤掉可能的 null 值
      const validImages = uploadedImages.filter((img): img is ProductImage => img !== null);

      // 4. 创建商品数据
      const productData: Product = {
        ...parsedInfo,
        id: productId,
        price: product.price,
        images: validImages,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'active' as const,
        currency: 'CNY',
        materials: [],
        comparison: {
          original: {
            price: product.price * 2, // 原版价格设为复刻价格的两倍
            material: parsedInfo.comparison.original.material,
            craftsmanship: parsedInfo.comparison.original.craftsmanship
          },
          replica: {
            price: product.price,
            material: parsedInfo.comparison.replica.material,
            craftsmanship: parsedInfo.comparison.replica.craftsmanship
          }
        },
        translations: {
          title: translationResult.title || '',
          description: translationResult.description || '',
          tags: Array.isArray(translationResult.tags) ? translationResult.tags : [],
          comparison: {
            original: {
              material: translationResult.comparison?.original?.material || '',
              craftsmanship: translationResult.comparison?.original?.craftsmanship || ''
            },
            replica: {
              material: translationResult.comparison?.replica?.material || '',
              craftsmanship: translationResult.comparison?.replica?.craftsmanship || ''
            }
          }
        }
      };

      // 5. 保存商品
      await createProduct(productData);
      result.success++;

    } catch (error) {
      console.error(`Error processing product ${i + 1}:`, error);
      result.failed++;
      result.errors.push({
        index: i,
        error: error instanceof Error ? error.message : '未知错误'
      });
    }
  }

  return result;
}; 